// Copyright (c) 2026 BlueRock Security, Inc.
// All rights reserved.
//
// BLUEROCK SECURITY, INC. PROPRIETARY AND CONFIDENTIAL INFORMATION

import { forwardRef, useState } from 'react';
import { Button, theme } from 'antd';
import type { ButtonProps } from 'antd';

export interface FilterActivatorProps extends ButtonProps {
  /** Optional left-side icon (or right-side if `iconRight`). */
  chipIcon?: React.ReactNode;
  /** When true, the chipIcon sits on the right and doubles as the clear-X. */
  iconRight?: boolean;
  /** Count badge — when > 0, shows as a rounded numeric pill. */
  count?: number;
  /** When provided, the badge / right-side icon becomes a clear-X on hover. */
  onClear?: () => void;
}

/**
 * The chip-shaped button used by the FilterBar's filter activators.
 * Rounded antd Button + optional left/right icon + optional count
 * badge that doubles as a clear-X on hover.
 *
 * Ported from bluerock-console/src/components/FilterBar.tsx, then
 * from agate into corenet.
 *
 * Wrapped in forwardRef so antd Popover can attach a ref to the
 * trigger and compute its position — without this, the popover gets
 * rendered at antd's off-screen measurement coordinates and never
 * animates into view.
 */
export const FilterActivator = forwardRef<HTMLButtonElement, FilterActivatorProps>(function FilterActivator({
  children,
  chipIcon,
  iconRight,
  count,
  onClear,
  style,
  ...rest
}, ref) {
  const { token } = theme.useToken();
  const [hoveringBadge, setHoveringBadge] = useState(false);

  const badge = (count ?? 0) > 0 ? (
    <span
      onMouseEnter={() => setHoveringBadge(true)}
      onMouseLeave={() => setHoveringBadge(false)}
      onClick={onClear ? (e) => { e.stopPropagation(); onClear(); } : undefined}
      style={{
        background: hoveringBadge && onClear ? token.colorTextSecondary : token.colorPrimary,
        color: '#fff',
        borderRadius: 100,
        padding: '0 5px',
        fontSize: 11,
        lineHeight: '16px',
        fontWeight: 500,
        cursor: onClear ? 'pointer' : undefined,
        transition: 'background 0.15s',
        display: 'inline-block',
        textAlign: 'center',
        minWidth: `${Math.max(String(count).length * 7, 10) + 10}px`,
        flexShrink: 0,
      }}
    >
      {hoveringBadge && onClear ? '✕' : count}
    </span>
  ) : null;

  const rightIcon = iconRight ? (
    <span
      onMouseEnter={() => setHoveringBadge(true)}
      onMouseLeave={() => setHoveringBadge(false)}
      onClick={onClear ? (e) => { e.stopPropagation(); onClear(); } : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 16,
        flexShrink: 0,
        cursor: onClear ? 'pointer' : undefined,
        color: hoveringBadge && onClear ? token.colorTextSecondary : token.colorTextTertiary,
        transition: 'color 0.15s',
      }}
    >
      {hoveringBadge && onClear ? '✕' : chipIcon}
    </span>
  ) : null;

  return (
    <Button
      ref={ref}
      shape="round"
      style={{
        height: 32,
        paddingInline: 12,
        fontSize: 14,
        borderColor: token.colorBorder,
        background: token.colorBgContainer,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        maxWidth: 280,
        overflow: 'hidden',
        ...(hoveringBadge ? { color: token.colorText, borderColor: token.colorBorder } : {}),
        ...style,
      }}
      {...rest}
    >
      {!iconRight && chipIcon && <span style={{ display: 'inline-flex', flexShrink: 0 }}>{chipIcon}</span>}
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {children}
      </span>
      {iconRight ? rightIcon : badge}
    </Button>
  );
});

export default FilterActivator;
